import _taggedTemplateLiteral from "@babel/runtime/helpers/esm/taggedTemplateLiteral";
var _templateObject, _templateObject2;
import React from 'react';
import _ from 'lodash';
import { Input, Header, Message, Button, Form, Dimmer, Loader, Divider } from 'semantic-ui-react';
import { withFormik } from 'formik';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import compose from 'lodash/flowRight';
import { AuthClient, EnjoyIcon } from '@emyw/core-lib';
import queryString from 'query-string';
import { normalizeErrors } from '../utils';
import ButtonLink from '../ButtonLink';
import '../primitives.css';

// const { REACT_APP_AUTH_CLIENT_IMAGE_PATH } = process.env;

const ResetPasswordLogic = withFormik({
  validateOnBlur: false,
  validateOnChange: false,
  // Transform outer props into form values
  mapPropsToValues: () => ({
    email: '',
    password: '',
    passwordConfirm: ''
  }),
  // Submission handler
  handleSubmit: async (values, _ref) => {
    let {
      props: {
        validateMutation,
        resetMutation,
        props
      },
      setErrors,
      setStatus,
      setSubmitting,
      setFieldValue,
      status
    } = _ref;
    /* Get the reset token from the url */
    try {
      const resetToken = queryString.parse(props.location.search).t;
      if (!resetToken) {
        setStatus('noLink');
        /** If we are in a status of validatingLink then call the validate mutation
         * This will check if the link matches a specific user and if it has not expired */
      } else if (values.email === '' && (!status || status === '')) {
        setStatus('validatingLink');
        const response = await validateMutation({
          variables: {
            resetToken
          }
        });
        const {
          ok,
          email,
          errors
        } = response.data.validateResetPasswordLink;
        if (ok) {
          setStatus('validLink');
          setFieldValue('email', email);
        } else {
          setStatus('invalidLink');
          setErrors(normalizeErrors(errors));
        }
        // if form submitted with a valid link, check the new passwords match and call reset mutation
      } else if (status === 'validLink' || values.email !== '') {
        if (values.password.length < 5) {
          setErrors(normalizeErrors([{
            path: 'password',
            message: 'Password of at least 5 characters required'
          }]));
        } else if (values.password !== values.passwordConfirm) {
          setErrors(normalizeErrors([{
            path: 'password',
            message: 'Passwords do not match'
          }]));
        } else {
          setErrors([]);
          const {
            password
          } = values;
          const response = await resetMutation({
            variables: {
              resetToken,
              password
            }
          });
          const {
            ok,
            errors
          } = response.data.resetPassword;
          if (ok) {
            setStatus('resetSuccess');
          } else {
            setErrors(normalizeErrors(errors));
          }
        }
      } else {
        setStatus('');
      }
    } catch (err) {
      setStatus('noLink');
      // log.trace(err);
    }
    setSubmitting(false);
  }
});
let submitted = false;

/** Reset Password Form is intended as a general purpose reset password page or dialog
 * <br /><br />
 * <hr />
 * <br />
 * <div>
 * <h4>Back-end Requirements</h4>
 * A backend graphql api endpoint with the validateResetPasswordLink and resetPassword mutations,
 * <a rel=noopener href="https://github.com/emyw/auth-api">auth-api</a>,
 * is needed for this form to function.
 * <h4>Reset Password</h4>
 * Current use case is that the user has received a reset password link through email (i.e. from the forgot password component).<br />
 * When clicked it will navigate to a page with this component which will check that the link is valid and not expired
 * (using the validateResetPasswordLink mutation). If valid, the form will provide password and confirm password fields for the user
 * to specify a new password.<br />
 * Upon clicking submit, assuming validation checks ok, the resetPassword mutation is called.
 * </div>
 */
const ResetPasswordForm = _ref2 => {
  let {
    submitForm,
    status,
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    errors,
    isSubmitting,
    onLogin,
    onForgotAgain,
    forceStatus
  } = _ref2;
  /* When initializing the page, first we will validate the reset link code */
  const localStatus = status || forceStatus;
  if (!localStatus && values.email === '' && !submitted) {
    submitted = true;
    submitForm();
  }
  return /*#__PURE__*/React.createElement("div", null, (!localStatus || localStatus === 'validatingLink') && /*#__PURE__*/React.createElement(Dimmer, {
    active: true
  }, /*#__PURE__*/React.createElement(Loader, {
    indeterminate: true
  }, "Processing Link...")), localStatus === 'validLink' && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Header, {
    color: "blue",
    attached: "top"
  }, /*#__PURE__*/React.createElement(EnjoyIcon, {
    className: "formTitleIcon"
  }), " \xA0 Reset Password for: ", values.email), /*#__PURE__*/React.createElement(Header, {
    block: true,
    attached: "bottom",
    as: "h5"
  }, "Please enter a new password."), /*#__PURE__*/React.createElement(Divider, {
    hidden: true
  }), /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement(Form.Field, {
    error: !!errors.password
  }, /*#__PURE__*/React.createElement(Input, {
    name: "password",
    id: "password",
    onChange: handleChange,
    onBlur: handleBlur,
    value: values.password,
    icon: "lock",
    type: "password",
    iconPosition: "left",
    placeholder: "Password",
    fluid: true
  })), /*#__PURE__*/React.createElement(Form.Field, {
    error: !!errors.passwordConfirm
  }, /*#__PURE__*/React.createElement(Input, {
    name: "passwordConfirm",
    id: "passwordConfirm",
    onChange: handleChange,
    onBlur: handleBlur,
    value: values.passwordConfirm,
    icon: "lock",
    type: "password",
    iconPosition: "left",
    placeholder: "Confirm Password",
    fluid: true
  })), /*#__PURE__*/React.createElement(Divider, {
    hidden: true
  }), /*#__PURE__*/React.createElement(Form.Field, {
    style: {
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement(Button, {
    className: "square",
    type: "submit",
    disabled: isSubmitting,
    onClick: handleSubmit,
    size: "large",
    primary: true,
    fluid: true,
    style: {
      margin: '0px auto',
      maxWidth: '300px'
    }
  }, "SUBMIT")), /*#__PURE__*/React.createElement(Form.Field, {
    style: {
      textAlign: 'center'
    }
  }, "Remember Password?", /*#__PURE__*/React.createElement(ButtonLink, {
    color: "blue",
    onClick: onLogin
  }, "\xA0\xA0Log In"))), (errors.password || errors.passwordConfirm || errors.resetToken || errors.update) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Divider, {
    hidden: true
  }), /*#__PURE__*/React.createElement(Message, {
    size: "small",
    error: true,
    header: "Trouble resetting password",
    list: _.concat(errors.password, errors.passwordConfirm, errors.resetToken, errors.update)
  }))), localStatus === 'resetSuccess' && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Message, {
    attached: "top"
  }, /*#__PURE__*/React.createElement(Message.Header, null, "Success!")), /*#__PURE__*/React.createElement(Message, {
    attached: "bottom"
  }, "Your password has been reset.", /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Button, {
    color: "blue",
    fluid: true,
    basic: true,
    onClick: onLogin
  }, "Log In"))), (localStatus === 'noLink' || localStatus === 'invalidLink') && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Message, {
    attached: "top"
  }, /*#__PURE__*/React.createElement(Message.Header, null, localStatus === 'noLink' ? 'Invalid Link' : 'Expired or invalid link')), /*#__PURE__*/React.createElement(Message, {
    attached: "bottom"
  }, errors.password || errors.passwordConfirm || errors.resetToken || errors.update ? /*#__PURE__*/React.createElement(React.Fragment, null, "Oops. Something went wrong:", /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Message, {
    size: "tiny",
    error: true,
    header: "Trouble resetting password",
    list: _.concat(errors.password, errors.passwordConfirm, errors.resetToken, errors.update)
  }), /*#__PURE__*/React.createElement(Divider, {
    hidden: true
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, "Oops. Something went wrong. Perhaps it took too long for the reset link to be used from when it was issued.", /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("br", null)), /*#__PURE__*/React.createElement(Button, {
    color: "blue",
    fluid: true,
    basic: true,
    onClick: onForgotAgain
  }, "Try Again?"))));
};
const validateResetPasswordLinkMutation = gql(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    mutation ($resetToken: String!) {\n        validateResetPasswordLink(resetToken: $resetToken) {\n            ok\n            email\n            errors {\n                path\n                message\n            }\n        }\n    }\n"])));
const resetPasswordMutation = gql(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    mutation ($resetToken: String!, $password: String!) {\n        resetPassword(resetToken: $resetToken, password: $password) {\n            ok\n            email\n            errors {\n                path\n                message\n            }\n        }\n    }\n"])));
ResetPasswordForm.defaultProps = {
  forceStatus: undefined
};
export default compose(graphql(validateResetPasswordLinkMutation, {
  name: 'validateMutation',
  client: AuthClient
}), graphql(resetPasswordMutation, {
  name: 'resetMutation',
  client: AuthClient
}), ResetPasswordLogic)(ResetPasswordForm);